// components/Attendance/CoaForm.jsx
// COA (Change of Attendance) request form — Velzon-styled.
// Replaces VB.NET isCOA=true branch: single date + modified start/end time pickers.

import React from "react";
import {
    Row, Col, Card, CardBody, CardHeader,
    FormGroup, Label, Input, Button, Spinner, Badge,
} from "reactstrap";
import useCoaForm from "./useCoaForm";
import DirectReportPicker from "./components/DirectReportPicker";
import DeleteRequestButton from "./components/DeleteRequestButton";

// Reuse approval box from FtoForm (or import from shared file)
const ApprovalBox = ({ title, approvalKey, noteKey, form, setField, disabled }) => (
    <div className="border rounded" style={{ overflow: "hidden" }}>
        <div className="px-3 py-2 bg-light border-bottom">
            <span style={{ fontSize: 12, fontWeight: 600, color: "var(--ssw-fg-1, #495057)" }}>{title}</span>
        </div>
        <div className="p-3">
            {[
                { val: true, lbl: "Approved", cls: "text-success" },
                { val: null, lbl: "Pending", cls: "text-warning" },
                { val: false, lbl: "Denied", cls: "text-danger" },
            ].map(({ val, lbl, cls }) => (
                <div className="form-check mb-1" key={lbl}>
                    <input
                        className="form-check-input" type="radio" name={approvalKey}
                        checked={JSON.stringify(form[approvalKey]) === JSON.stringify(val)}
                        disabled={disabled}
                        onChange={() => setField(approvalKey, val)}
                    />
                    <label className={`form-check-label fw-medium ${cls}`} style={{ fontSize: 13 }}>
                        {lbl}
                    </label>
                </div>
            ))}
            <div className="mt-2">
                <label className="text-muted mb-1" style={{ fontSize: 11 }}>Note</label>
                <Input type="textarea" bsSize="sm" rows={2}
                    value={form[noteKey] ?? ""} disabled={disabled}
                    placeholder={disabled ? "—" : "Add note..."}
                    onChange={(e) => setField(noteKey, e.target.value)} />
            </div>
        </div>
    </div>
);

const CoaForm = ({ coaId = 0 }) => {
    const {
        form, setField, loading, saving, isNew, auth,
        canEditEmployee, canEditSupervisor, canEditHR,
        handleSubmit,
        selectEmployee,
        workedPreview,
        dayHoursText,
    } = useCoaForm({ coaId });

    if (loading) return (
        <div className="d-flex justify-content-center py-5"><Spinner color="primary" /></div>
    );

    const ro = !canEditEmployee;

    const status =
        form.hrApproval === true ? { label: "HR Approved", color: "success" } :
            form.hrApproval === false ? { label: "HR Denied", color: "danger" } :
                { label: "Pending", color: "warning" };

    return (
        <Row className="g-3 justify-content-center">
            <Col xl={8} lg={9}>
                <Card>
                    <CardHeader className="d-flex align-items-center gap-2 py-3">
                        <h5 className="card-title mb-0 flex-grow-1" style={{ fontSize: 15 }}>
                            {isNew ? "New COA Request" : `COA Request #${coaId}`}
                        </h5>
                        <Badge color={status.color} pill>{status.label}</Badge>
                    </CardHeader>
                    <CardBody>

                        {/* Employee chip */}
                        <div className="d-flex align-items-center gap-2 rounded p-2 mb-3"
                            style={{ background: "var(--ssw-tint-blue, #eef2ff)" }}>
                            <div className="rounded-circle d-flex align-items-center justify-content-center fw-bold text-white"
                                style={{ background: "var(--ssw-primary-fill, #405189)", width: 32, height: 32, fontSize: 12, flexShrink: 0 }}>
                                {form.empId?.slice(0, 2).toUpperCase()}
                            </div>
                            <div>
                                <div className="fw-semibold" style={{ fontSize: 13, color: "var(--ssw-ink-blue, #405189)" }}>
                                    {auth.empId === form.empId ? "My Request" : form.empName ?? form.empId}
                                </div>
                                <div style={{ fontSize: 11, color: "var(--ssw-fg-2, #878a99)" }}>EMP ID: {form.empId}</div>
                            </div>
                        </div>

                        {/* Go-live parity (2026-09-24), ATT#6: supervisors may file for an active direct report. */}
                        {isNew && (
                            <DirectReportPicker
                                enabled
                                selfEmpId={auth.empId}
                                value={form.empId}
                                onChange={selectEmployee}
                            />
                        )}

                        {/* ── Date ────────────────────────────────────────────────── */}
                        <p className="text-uppercase fw-semibold mb-2"
                            style={{ fontSize: 11, color: "var(--ssw-fg-2, #878a99)", letterSpacing: ".5px" }}>Date</p>
                        <Row className="g-2 mb-3 align-items-end">
                            <Col xs="auto">
                                <FormGroup className="mb-0">
                                    <Label style={{ fontSize: 12 }}>Date</Label>
                                    <Input type="date" bsSize="sm" style={{ width: 160 }}
                                        value={form.startDate ?? ""} disabled={ro}
                                        onChange={(e) => setField("startDate", e.target.value)} />
                                </FormGroup>
                            </Col>
                            <Col xs="auto" className="pb-1">
                                <span className="text-muted" style={{ fontSize: 12 }}>to</span>
                            </Col>
                            <Col xs="auto">
                                <FormGroup className="mb-0">
                                    <Label style={{ fontSize: 12 }}>
                                        End date <span className="text-muted fw-normal">(optional)</span>
                                    </Label>
                                    <div className="d-flex gap-1">
                                        <Input type="date" bsSize="sm" style={{ width: 160 }}
                                            value={form.endDate ?? ""} disabled={ro}
                                            onChange={(e) => setField("endDate", e.target.value)} />
                                        {form.endDate && !ro && (
                                            <Button size="sm" color="light" className="px-2"
                                                onClick={() => setField("endDate", "")}>✕</Button>
                                        )}
                                    </div>
                                </FormGroup>
                            </Col>
                        </Row>

                        {/* ── Modified times ───────────────────────────────────────── */}
                        {/* Owner 2026-10-12: people typed the hours they were AWAY ("8 to 10")
                            instead of the hours they will work ("10 to 4:30"), as the legacy
                            "Modified Start / Modified End" labels did not say which. */}
                        <p className="text-uppercase fw-semibold mb-1"
                            style={{ fontSize: 11, color: "var(--ssw-fg-2, #878a99)", letterSpacing: ".5px" }}>Time you will be away</p>
                        <p className="mb-2" style={{ fontSize: 12, color: "var(--ssw-fg-2, #878a99)" }}>
                            Example: a doctor's visit from 8:00 to 10:00 AM → away from 8:00 AM until 10:00 AM.
                            The hours you will work that day are worked out from it ({dayHoursText}).
                        </p>
                        <Row className="g-2 mb-1">
                            <Col md={4}>
                                <FormGroup className="mb-0">
                                    <Label style={{ fontSize: 12 }}>Away from</Label>
                                    <Input type="time" bsSize="sm"
                                        value={form.awayFrom ?? ""} disabled={ro}
                                        onChange={(e) => setField("awayFrom", e.target.value)} />
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup className="mb-0">
                                    <Label style={{ fontSize: 12 }}>Away until</Label>
                                    <Input type="time" bsSize="sm"
                                        value={form.awayUntil ?? ""} disabled={ro}
                                        onChange={(e) => setField("awayUntil", e.target.value)} />
                                </FormGroup>
                            </Col>
                        </Row>
                        <p className="mb-3" style={{ fontSize: 12, color: "var(--ssw-fg-2, #878a99)" }}>
                            Hours that day: <b>{workedPreview || (form.modifiedStartTime || form.modifiedEndTime
                                ? `${form.modifiedStartTime || "—"} to ${form.modifiedEndTime || "—"}` : "—")}</b>
                        </p>

                        {/* ── Employee comment ─────────────────────────────────────── */}
                        <FormGroup className="mb-3">
                            <Label style={{ fontSize: 12 }}>Employee's comments</Label>
                            <Input type="textarea" rows={3}
                                value={form.employeeComment} readOnly={ro}
                                placeholder="Describe the reason for this schedule change..."
                                onChange={(e) => setField("employeeComment", e.target.value)} />
                        </FormGroup>

                        {/* ── Approvals (no MPC for COA) ───────────────────────────── */}
                        <p className="text-uppercase fw-semibold mb-2"
                            style={{ fontSize: 11, color: "var(--ssw-fg-2, #878a99)", letterSpacing: ".5px" }}>Approvals</p>
                        <Row className="g-2 mb-4">
                            <Col md={6}>
                                <ApprovalBox title="Supervisor"
                                    approvalKey="supervisorApproval" noteKey="supervisorNote"
                                    form={form} setField={setField} disabled={!canEditSupervisor} />
                            </Col>
                            <Col md={6}>
                                <ApprovalBox title="Human Resources"
                                    approvalKey="hrApproval" noteKey="hrNote"
                                    form={form} setField={setField} disabled={!canEditHR} />
                            </Col>
                        </Row>

                        <div className="d-flex justify-content-end gap-2">
                            {!isNew && <DeleteRequestButton kind="coa" id={coaId} row={form} auth={auth} />}
                            <Button color="light" tag="a" href="/attendance">Cancel</Button>
                            <Button color="primary" disabled={saving} onClick={handleSubmit}>
                                {saving && <Spinner size="sm" className="me-1" />}
                                {isNew ? "Submit Request" : "Save Changes"}
                            </Button>
                        </div>

                    </CardBody>
                </Card>
            </Col>
        </Row>
    );
};

export default CoaForm;